/* eslint-disable react/jsx-props-no-spreading */
import {I18n, MessageDescriptor, setupI18n} from '@lingui/core';
import {DateTime, DateTimeFormatOptions} from 'luxon';
import React, {ComponentType, createContext, useContext} from 'react';

import {patchDateTimeFormat} from '../../src/helpers/dateTimeHelpers';
import {Localizer} from '../../src/store/stateless/internationalizationSelectors';

/*
 * Props.
 */

export interface InternationalizationContextProps {
  i18n: I18n;
  localize: Localizer;
}

export type LocalizeRenderer = (localize: Localizer) => string;

/*
 * Context.
 */

const InternationalizationContext = createContext<InternationalizationContextProps>({
  i18n: setupI18n({language: 'en', catalogs: {en: {} as any}}),
  localize: (descriptorOrDateTime: MessageDescriptor | DateTime, format?: DateTimeFormatOptions) => {
    // DateTime localization.
    if (DateTime.isDateTime(descriptorOrDateTime)) {
      return descriptorOrDateTime.toLocaleString(patchDateTimeFormat(format || DateTime.DATETIME_FULL));
    }

    // String localization.
    return descriptorOrDateTime.defaults || '';
  },
});

export const InternalizationContextProvider = InternationalizationContext.Provider;

/*
 * Hook.
 */

export function useLocalize() {
  return useContext(InternationalizationContext).localize;
}

export function withInternationalization<T>(
  Component: ComponentType<T>,
): ComponentType<Omit<T, keyof InternationalizationContextProps>> {
  return function (props) {
    return (
      <InternationalizationContext.Consumer>
        {(context) => <Component {...(props as T)} {...context} />}
      </InternationalizationContext.Consumer>
    );
  };
}
